'use client';

import { useEffect, useState } from 'react';
import { TrashIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useChatMemories } from '@/hooks/queries/useChatMemories';
import { ApiError } from '@/lib/api';
import { track } from '@/lib/telemetry';
import type { ChatMemory } from '@/types/chat';

const LEARNED_ON = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

/**
 * "O que a Unare lembra sobre você" on Perfil (VER-546): every sentence the chat keeps about the
 * person (VER-545), when it was learned, and a delete. Trust and LGPD: nothing is remembered that
 * the person cannot see and remove here. A Worker without the route (404) renders nothing; any other
 * failure offers a retry, over the list it already shows when a refresh is what failed.
 */
export function ChatMemoriesSection() {
  const { data, error, isLoading, mutate, forget } = useChatMemories();
  const missing = error instanceof ApiError && error.status === 404;

  function retry() {
    track('friction.retry_clicked', { surface: 'chat_memories' });
    void mutate();
  }

  if (missing) return null;

  return (
    <section data-testid="chat-memories" aria-busy={isLoading} className="lg:pl-8">
      <SectionHeader count={data?.total} limit={data?.limit} />
      <MemoriesLoading loading={!data && !error} />
      <LoadFailed error={error} onRetry={retry} />
      <NoMemories memories={data?.memories} />
      <MemoryList memories={data?.memories} onForget={forget} />
    </section>
  );
}

function SectionHeader({ count, limit }: { count: number | undefined; limit: number | undefined }) {
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between gap-4">
        <p className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
          O que a Unare lembra sobre você
        </p>
        <MemoryCount count={count} limit={limit} />
      </div>
      <p className="mt-1 text-sm text-text-secondary">
        Preferências e fatos que você contou à Unare, no app e no WhatsApp, para ela levar às próximas conversas.
        Tudo o que ela guarda está aqui, e você apaga quando quiser.
      </p>
    </div>
  );
}

function MemoryCount({ count, limit }: { count: number | undefined; limit: number | undefined }) {
  if (count === undefined || limit === undefined || count === 0) return null;
  return (
    <p className="shrink-0 text-xs text-text-tertiary">
      {count} de {limit}
    </p>
  );
}

function MemoriesLoading({ loading }: { loading: boolean }) {
  if (!loading) return null;
  return (
    <div className="space-y-2">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  );
}

function LoadFailed({ error, onRetry }: { error: Error | undefined; onRetry: () => void }) {
  // Keyed on the error itself: a retry that fails again is another failure to count.
  useEffect(() => {
    if (error) track('friction.load_failed', { surface: 'chat_memories' });
  }, [error]);

  if (!error) return null;
  return (
    <div role="alert" className="flex items-center justify-between gap-4 rounded-lg border border-error-border p-4 text-sm">
      <p className="text-text-secondary">Não foi possível carregar esta seção.</p>
      <Button type="button" variant="outline" size="sm" className="cursor-pointer" onClick={onRetry}>
        Tentar novamente
      </Button>
    </div>
  );
}

function NoMemories({ memories }: { memories: ChatMemory[] | undefined }) {
  if (!memories || memories.length > 0) return null;
  return (
    <div className="rounded-lg border border-dashed border-border-subtle p-4 text-sm text-text-secondary">
      <p className="font-semibold text-text-primary">Nada guardado ainda.</p>
      <p className="mt-1">
        Diga à Unare o que lembrar, como “lembre que prefiro respostas curtas”, e ela leva isso para as próximas
        conversas.
      </p>
    </div>
  );
}

function MemoryList({
  memories,
  onForget,
}: {
  memories: ChatMemory[] | undefined;
  onForget: (id: string) => Promise<void>;
}) {
  if (!memories || memories.length === 0) return null;
  return (
    <ul className="divide-y divide-border-subtle">
      {memories.map((memory) => (
        <MemoryRow key={memory.id} memory={memory} onForget={onForget} />
      ))}
    </ul>
  );
}

function MemoryRow({ memory, onForget }: { memory: ChatMemory; onForget: (id: string) => Promise<void> }) {
  const [forgetting, setForgetting] = useState(false);

  async function forgetMemory() {
    setForgetting(true);
    try {
      await onForget(memory.id);
      track('action.chat.forget_memory');
    } catch {
      setForgetting(false);
      toast.error('Não consegui apagar agora. Tente de novo.');
    }
  }

  return (
    <li className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-sm break-words text-text-primary">{memory.text}</p>
        <p className="mt-0.5 text-xs text-text-tertiary">Aprendido em {LEARNED_ON.format(new Date(memory.createdAt))}</p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0 cursor-pointer text-text-secondary"
        disabled={forgetting}
        aria-label={`Apagar: ${memory.text}`}
        onClick={forgetMemory}
      >
        <TrashIcon className="size-4" />
      </Button>
    </li>
  );
}
